import './index.scss';
import {Card, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Skeleton, TextField, Tooltip, Typography} from "@mui/material";
import Divider from "@mui/material/Divider";
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import Grid from "@mui/material/Unstable_Grid2";
import useIsMounted from "../../../hooks/use-is-mounted";
import {AddOutlined, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import {Popover} from "../../app-specific/popover";
import SearchInput from "../../base/input/search_input";
import UpsertProductTechnicalPropertyDialog from "../upsert-product-technical-property";
import SuggestTechnicalPropertyDialog from "../suggest-technical-property";
import ManageTechnicalPropertyTemplatesDialog from "../manage-technical-property-templates-dialog";
import {numComparator, stringComparator} from "../../../../core/services/utils/utils";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import DownloadAffectedProductsDialog from "../download-affected-products";
import {Checkbox} from "../../base/checkbox/checkbox";
import {ReactComponent as Icon} from "../../../../assets/images/part-support-request/checkbox.svg";
import {ReactComponent as IconCheckbox} from "../../../../assets/images/part-support-request/checkbox-active.svg";
import {ReactComponent as SaveIcon} from "../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../assets/images/save-rollback/cancel.svg";
import classnames from "classnames";
import ToastService from "../../../../core/services/toast";
import Api from "../../../../core/services/api/api";
import axios from "axios";
import {ProductTechnicalPropertyTypeIds} from "../../../../core/constants/enums";
import useAccessLevels from "../../../hooks/use-access-levels";

const initialSuggestTechnicalPropertyDialogState = {open: false};
const initialAddCustomPropertyDialogState = {open: false,}
const initialManageTechnicalPropertyTemplatesDialogState = {open: false, selectedPropertyIds: [], templates: []};
const initialUpdatePropertyDialogState = {open: false, property: null};
const initialAffectedProductsDialogState = {open: false, productsList: [], message: '', fileTitle: ''};


const ManageProductTechnicalPropertiesDialog = ({
                                                    open,
                                                    onClose,
                                                    productId,
                                                    industryId,
                                                    industryTitle,
                                                    selectedPropertyIds: selectedPropertyIdsProps,
                                                    onSubmit,
                                                }) => {
    const {packages} = useAccessLevels();
    const [keywords, setKeywords] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [suggestTechnicalPropertyDialogState, setSuggestTechnicalPropertyDialogState] = useState(initialSuggestTechnicalPropertyDialogState);
    const [addCustomPropertyDialogState, setAddCustomPropertyDialogState] = useState(initialAddCustomPropertyDialogState);
    const [updatePropertyDialogState, setUpdatePropertyDialogState] = useState(initialUpdatePropertyDialogState);
    const [manageTechnicalPropertyTemplatesDialogState, setManageTechnicalPropertyTemplatesDialogState] = useState(initialManageTechnicalPropertyTemplatesDialogState);
    const [removingCustomIds, setRemovingCustomIds] = useState([]);
    const [affectedProductsDialogState, setAffectedProductsDialogState] = useState(initialAffectedProductsDialogState);
    const [loading, setLoading] = useState(false);
    const [properties, setProperties] = useState([]);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [propertyOrders, setPropertyOrders] = useState([])

    const typeNames = React.useMemo(() => ({
        [ProductTechnicalPropertyTypeIds.text]: "Text",
        [ProductTechnicalPropertyTypeIds.number]: "Number",
        [ProductTechnicalPropertyTypeIds.date]: "Date",
        [ProductTechnicalPropertyTypeIds.boolean]: "Boolean",
        [ProductTechnicalPropertyTypeIds.decimal]: "Decimal",
        [ProductTechnicalPropertyTypeIds.money]: "Money",
        [ProductTechnicalPropertyTypeIds.fraction]: "Fraction",
        [ProductTechnicalPropertyTypeIds.color]: "Color",
    }), [])

    const templatesRef = useRef(null);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    const visibleProperties = useMemo(() => {
        const visible = properties.filter(e => {
            return (
                e.title?.toLowerCase().includes(keywords?.toLowerCase())
                || e.description?.toLowerCase().includes(keywords?.toLowerCase())
                || typeNames[e.typeId]?.toLowerCase().includes(keywords?.toLowerCase())
            )
        })
        const ordered = [];
        for (const id of propertyOrders) {
            const found = visible.find((item) => item.id === id);
            if (found)
                ordered.push(found);
        }
        const rest = visible.filter((item) => !ordered.includes(item));
        rest.sort((a, b) => a.title.localeCompare(b.title));
        ordered.push(...rest);
        return ordered;
    }, [keywords, properties, propertyOrders, typeNames])

    /**
     * Load properties for the selected template
     */
    useEffect(() => {
        if (open) {
            setSelectedTemplateId(null);
            setKeywords('');
            setSelectedPropertyIds([]);
        }
    }, [open]);

    /**
     * Opens the dialog for suggesting a new technical property
     * @type {(function(): void)}
     */
    const suggestStandardProperty = useCallback((onLeave) => {
        if (!packages.lookup.submitTechnicalSuggestions) {
            return;
        }
        onLeave();
        setSuggestTechnicalPropertyDialogState({open: true});
    }, [packages.lookup.submitTechnicalSuggestions])

    /**
     * Opens the dialog for adding a custom technical property
     * @type {(function(): void)}
     */
    const addCustomProperty = useCallback((onLeave) => {
        onLeave();
        setAddCustomPropertyDialogState({open: true});
    }, [])

    /**
     * Adds the newly created property to the list of properties
     * @type {(function(*): void)}
     */
    const onCustomPropertyCreated = useCallback((property) => {
        setProperties(prevState => {
            const newState = ([...prevState, property]);
            newState.sort((a, b) => {
                if (typeof a.orderIndex === 'number' && typeof b.orderIndex === 'number') {
                    return a.orderIndex - b.orderIndex;
                }
                return stringComparator(a.title, b.title);
            });
            return newState;
        });
    }, [])

    /**
     * Updates the selected property with its new values.
     * @type {(function(*): void)}
     */
    const onPropertyUpdated = useCallback((property) => {
        setProperties(prevState => prevState.map(e => e.id === property.id ? {...e, ...property} : e));
        if (updatePropertyDialogState.open && updatePropertyDialogState.property?.id === property.id) {
            setUpdatePropertyDialogState(prevState => ({...prevState, property: {...prevState.property, ...property}}));
        }
    }, [updatePropertyDialogState.open, updatePropertyDialogState.property?.id])

    /**
     * Submit the selected properties
     * @type {(function(*): void)}
     */
    const onTemplateSelected = useCallback((template) => {
        const selectedPropertyIds = template.selectedPropertyIds ?? [];
        setSelectedTemplateId(template.id);
        if (selectedTemplateId === template.id) {
            return;
        }
        const selectableSelectedPropertyIds = selectedPropertyIds.filter(e => !selectedPropertyIdsProps.includes(e));
        if (selectableSelectedPropertyIds.length < selectedPropertyIds.length) {
            ToastService.warning({
                title: "Duplicates",
                message: 'Some of the properties in the template already exist for this product and thus are skipped.'
            });
        }
        setSelectedPropertyIds(properties.filter(e => selectableSelectedPropertyIds.includes(e.id)).map(e => e.id));
    }, [properties, selectedPropertyIdsProps, selectedTemplateId])

    /**
     * Opens the dialog to save the selection as a template for this industry.
     * @type {(function(): void)}
     */
    const saveSelectionAsTemplate = useCallback(() => {
        setManageTechnicalPropertyTemplatesDialogState({
            open: true,
            selectedPropertyIds: [...selectedPropertyIds, ...(selectedPropertyIdsProps ?? [])],
            templates: templatesRef.current?.getTemplates() ?? [],
        });
    }, [selectedPropertyIds, selectedPropertyIdsProps])

    /**
     * Fetches properties for the selected industry
     * @type {(function(): Promise<void>)}
     */
    const getProperties = useCallback(async () => {
        setLoading(true);
        const response = await Api.getEligibleIndustryPropertiesByPartIds({
            partIds: [productId]
        })
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const allProperties = response?.data
                ?.map(e => ({
                    id: e.id,
                    title: e.title,
                    description: e.description,
                    typeId: e.industryPropertyTypeId,
                    hasPredefinedValues: e.config?.hasPreDefinedValues ?? false,
                    isMultiSelect: e.config?.isMultiSelect ?? false,
                    isDefault: e.isDefault ?? false,
                })) ?? [];
            allProperties.sort((a, b) => {
                if (typeof a.orderIndex !== 'number' || typeof b.orderIndex !== 'number')
                    return stringComparator(a.title, b.title);
                return numComparator(a.orderIndex, b.orderIndex);
            });
            setProperties(allProperties);
        }
    }, [productId, isMounted])

    /**
     * As soon as the dialog opens:
     * - fetches the properties of the industry.
     */
    useEffect(() => {
        if (!open)
            return;
        void getProperties();
    }, [getProperties, open]);

    /**
     * Fetches the property orders of the industry.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPropertyOrders = useCallback(async (industryId, cancelToken) => {
        const response = await Api.getIndustryPropertyExtraConfigs(industryId, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPropertyOrders(response?.data?.propertiesOrders ?? []);
        }
    }, [isMounted])

    /**
     * When the industry changes, fetch the property orders.
     */
    useEffect(() => {
        if (!open || !industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPropertyOrders(industryId, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [industryId, fetchPropertyOrders, open])

    /**
     * Opens the dialog for updating the property.
     * @type {(function(*): void)}
     */
    const openUpdatePropertyDialog = useCallback((property) => {
        setUpdatePropertyDialogState({open: true, property});
    }, [])

    /**
     * Gets the removal confirmation for the property
     * @type {(function(*, number=): void)}
     */
    const getRemoveConfirmation = useCallback((property, numPartsAffected = 0) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `Property: "${property.title}" with ${numPartsAffected} affected product${numPartsAffected === 1 ? '' : 's'}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setRemovingCustomIds(prevState => [...prevState, property.id]);
                const response = await Api.removeCustomIndustryProperty(property.id)
                if (!isMounted())
                    return;
                setRemovingCustomIds(prevState => prevState.filter(e => e !== property.id));
                if (response?.resultFlag) {
                    setSelectedPropertyIds(prevState => prevState.filter(e => e !== property.id));
                    if (numPartsAffected) {
                        setAffectedProductsDialogState({
                            open: true,
                            productsList: response.data.affectedParts.map(e => e.partNo),
                            message: `The following ${numPartsAffected} part${numPartsAffected === 1 ? ' is' : 's are'} affected by the removal of the property: "${property.title}. You may download a CSV file for your reference"`,
                            fileTitle: `affected parts from removal of ${property.title}.csv`,
                        })
                    }
                    setProperties(prevState => prevState.filter(e => e.id !== property.id));
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted])

    /**
     * Toggles the selection of a property
     * @type {(function(*): void)}
     */
    const onSelectionToggled = useCallback((property) => {
        setSelectedPropertyIds(prevState => {
            if (prevState.includes(property.id)) {
                return prevState.filter(e => e !== property.id);
            }
            return [...prevState, property.id];
        })
    }, [])

    /**
     * Submits the selected properties
     * @type {(function())}
     */
    const submitSelectedProperties = useCallback(async () => {
        setSubmitting(true);
        const addedProperties = [];
        const response = await Api.addPartIndustryProperties({
            partId: productId,
            industryPropertyIds: selectedPropertyIds.filter(e => !selectedPropertyIdsProps.includes(e)),
        })
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response.resultFlag) {
            const addedPropertyIds = response.data.map(e => e.industryPropertyId)
            addedProperties.push(...properties.filter(e => addedPropertyIds.includes(e.id)).map(e => ({
                ...e,
                partIndustryPropertyId: response.data.find(i => i.industryPropertyId === e.id).id
            })));
        }
        onSubmit(addedProperties);
        onClose();
    }, [productId, onSubmit, selectedPropertyIds, selectedPropertyIdsProps, properties, isMounted, onClose])

    const [standardProperties, customProperties] = useMemo(() => {
        const standardProperties = [];
        const customProperties = [];
        for (const property of visibleProperties) {
            const toBeAdded = {
                ...property,
                selected: selectedPropertyIds.includes(property.id) || selectedPropertyIdsProps.includes(property.id),
                selectionReadonly: selectedPropertyIdsProps.includes(property.id),
            }
            if (property.isDefault) {
                standardProperties.push(toBeAdded);
            } else {
                customProperties.push(toBeAdded);
            }
        }
        return [standardProperties, customProperties]
    }, [selectedPropertyIds, selectedPropertyIdsProps, visibleProperties])

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'md'}
                scroll={'paper'}
                className={'bound-dialog'}
            >
                <DialogTitle>
                    <Grid container spacing={2} sx={{margin: 0, padding: 3, paddingBlock: 0}}>
                        <Grid xs={12}>
                            <p className={`text-lg text-color-1 font-weight-semi-bold text-center`}>
                                Available properties for {industryTitle ?? ""}
                            </p>
                        </Grid>
                        <Grid xs>
                            <ProductTechnicalPropertiesTemplatesSelect
                                disabled={loading}
                                industryId={industryId}
                                selectedTemplateId={selectedTemplateId}
                                setSelectedTemplateId={setSelectedTemplateId}
                                onTemplateSelected={onTemplateSelected}
                                ref={templatesRef}
                            />
                        </Grid>
                        <Grid xs={'auto'}>
                            <Popover
                                child={(onEnter) => (
                                    <Button
                                        onClick={onEnter}
                                        color={'primary'}
                                        variant={'outlined'}
                                        size={'small'}
                                        className={'d-flex align-items-center'}
                                    >
                                        <AddOutlined sx={{marginInlineEnd: 0.5, padding: 0.25}}/>
                                        <Typography variant={'caption'} fontWeight={600}>
                                            Add Property
                                        </Typography>
                                    </Button>
                                )}
                                popoverChild={(onEnter, onLeave) => (
                                    <div className='d-flex flex-column align-items-center justify-content-center px-2'>
                                        {
                                            packages.lookup.submitTechnicalSuggestions &&
                                            <Button
                                                onClick={() => suggestStandardProperty(onLeave)}
                                                color={'inherit'}
                                                variant={'text'}
                                                sx={{padding: 1.5, fontWeight: 500, textAlign: 'start', width: '100%'}}
                                            >
                                                Suggest standard property
                                            </Button>
                                        }
                                        <Button
                                            onClick={() => addCustomProperty(onLeave)}
                                            color={'inherit'}
                                            variant={'text'}
                                            sx={{padding: 1.5, fontWeight: 500, textAlign: 'start', width: '100%'}}
                                        >
                                            Add custom property
                                        </Button>
                                    </div>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-4 py-3'}>
                    <div className={'d-flex flex-column'} style={{gap: 24}}>
                        <SearchInput
                            autoSearch
                            initialValue={keywords}
                            disabled={loading}
                            search={(val) => setKeywords(val ?? "")}
                            label={'Search'}
                            variant={'outlined'}
                            paperProps={{
                                className: 'px-0'
                            }}
                        />
                        <div className={'d-flex flex-column'} style={{gap: 12}}>
                            <Typography variant={'body1'} fontWeight={600}>
                                Standard Properties
                                <Typography component={'span'} variant={'caption'} fontWeight={500} sx={{marginInlineStart: 1}}>
                                    Available and Affective on Look-up System
                                </Typography>
                            </Typography>
                            {
                                loading &&
                                Array.from({length: 4}).map((_, i) => (
                                    <Skeleton
                                        key={`loading-${i}`}
                                        variant="rectangular"
                                        width={'100%'}
                                        sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                    />
                                ))
                            }
                            {
                                !loading && !standardProperties.length &&
                                <Typography variant={'body2'} sx={{marginBlock: 10}}>
                                    No standard properties found. you may suggest one using the button at the top.
                                </Typography>
                            }
                            {
                                !loading &&
                                standardProperties.map(property => (
                                    <ManageProductTechnicalPropertiesDialogProperty
                                        key={property.id}
                                        onSelectionToggled={onSelectionToggled}
                                        onUpdate={openUpdatePropertyDialog}
                                        property={property}
                                        typeNames={typeNames}
                                    />
                                ))
                            }
                        </div>
                        <div className={'d-flex flex-column'} style={{gap: 12}}>
                            <Typography variant={'body1'} fontWeight={600}>
                                Custom Properties
                                <Typography component={'span'} variant={'caption'} fontWeight={500} sx={{marginInlineStart: 1}}>
                                    Available in Part Information Only (not in Look-up)
                                </Typography>
                            </Typography>
                            {
                                loading &&
                                Array.from({length: 4}).map((_, i) => (
                                    <Skeleton
                                        key={`loading-${i}`}
                                        variant="rectangular"
                                        width={'100%'}
                                        sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                    />
                                ))
                            }
                            {
                                !loading && !customProperties.length &&
                                <Typography variant={'body2'} sx={{marginBlock: 10}}>
                                    No custom properties found. you may add one using the button at the top.
                                </Typography>
                            }
                            {
                                !loading &&
                                customProperties.map(property => (
                                    <ManageProductTechnicalPropertiesDialogProperty
                                        key={property.id}
                                        onUpdate={openUpdatePropertyDialog}
                                        onRemove={getRemoveConfirmation}
                                        removing={removingCustomIds.includes(property.id)}
                                        onSelectionToggled={onSelectionToggled}
                                        property={property}
                                        typeNames={typeNames}
                                    />
                                ))
                            }
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className={'p-0 w-100 d-flex flex-column'}>
                    <Divider className='divider'/>
                    <div className={'d-flex justify-content-between align-items-stretch'} style={{width: '-webkit-fill-available'}}>
                        <div className={'d-flex flex-column align-items-center justify-content-center p-2'}>
                            <Typography variant={'body2'} sx={{paddingInline: 1.5}}>
                                {selectedPropertyIds.length + (selectedPropertyIdsProps?.length ?? 0)} {selectedPropertyIds.length === 1 ? 'property' : 'properties'} selected.
                            </Typography>
                            {
                                (selectedPropertyIds.length + (selectedPropertyIdsProps?.length ?? 0)) > 0 &&
                                <div className={'d-flex align-items-center'}>
                                    <Button
                                        disabled={!open || submitting || !selectedPropertyIds.length}
                                        variant={'text'}
                                        size={'small'}
                                        color={'primary'}
                                        type={'button'}
                                        onClick={() => saveSelectionAsTemplate()}
                                    >
                                        {
                                            submitting
                                                ? 'submitting...'
                                                : "Save as a template"
                                        }
                                    </Button>
                                </div>
                            }
                        </div>
                        <div className={'d-flex justify-content-end flex-grow-1'}>
                            <Button
                                disabled={!open || submitting || !selectedPropertyIds.length}
                                variant={'dialog'}
                                color={'primary'}
                                type={'button'}
                                className={'h-100'}
                                borderRadius={'8px 0 8px 0'}
                                onClick={() => submitSelectedProperties()}
                            >
                                {submitting ? 'submitting...' : "Select"}
                            </Button>
                        </div>
                    </div>
                </DialogActions>
            </Dialog>
            <UpsertProductTechnicalPropertyDialog
                open={addCustomPropertyDialogState.open}
                onUpsert={onCustomPropertyCreated}
                onClose={() => setAddCustomPropertyDialogState(initialAddCustomPropertyDialogState)}
                industryId={industryId}
            />
            <UpsertProductTechnicalPropertyDialog
                open={updatePropertyDialogState.open}
                property={updatePropertyDialogState.property}
                onUpsert={onPropertyUpdated}
                onClose={() => setUpdatePropertyDialogState(initialUpdatePropertyDialogState)}
                industryId={industryId}
            />
            {
                packages.lookup.submitTechnicalSuggestions &&
                <SuggestTechnicalPropertyDialog
                    open={suggestTechnicalPropertyDialogState.open}
                    close={() => setSuggestTechnicalPropertyDialogState(initialSuggestTechnicalPropertyDialogState)}
                    industryId={industryId}
                />
            }
            <ManageTechnicalPropertyTemplatesDialog
                open={manageTechnicalPropertyTemplatesDialogState.open}
                selectedPropertyIds={manageTechnicalPropertyTemplatesDialogState.selectedPropertyIds}
                setOpen={() => setManageTechnicalPropertyTemplatesDialogState(initialManageTechnicalPropertyTemplatesDialogState)}
                industryId={industryId}
                selectedTemplateId={selectedTemplateId}
                templates={manageTechnicalPropertyTemplatesDialogState.templates}
                onTemplateUpserted={(template) => {
                    setSelectedTemplateId(template.id);
                    templatesRef.current?.addOrReplaceTemplate(template);
                }}
            />
            <DownloadAffectedProductsDialog
                open={affectedProductsDialogState.open}
                onClose={() => setAffectedProductsDialogState(initialAffectedProductsDialogState)}
                productList={affectedProductsDialogState.productsList}
                message={affectedProductsDialogState.message}
                fileTitle={affectedProductsDialogState.fileTitle}
            />
        </>
    );
}

const ProductTechnicalPropertiesTemplatesSelect = forwardRef(({
                                                                  disabled,
                                                                  industryId,
                                                                  selectedTemplateId,
                                                                  setSelectedTemplateId,
                                                                  onTemplateSelected: onTemplateSelectedProps
                                                              }, ref) => {
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Expose addTemplate function to parent component
     */
    useImperativeHandle(ref, () => ({
        addOrReplaceTemplate: (template) => {
            setTemplates(prevState => {
                const found = prevState.find(e => e.id === template.id);
                if (!found) {
                    return [...prevState, template]
                }
                return prevState.map(e => e.id === template.id ? {...e, ...template} : e);
            })
        },
        getTemplates: () => templates,
    }), [templates])

    /**
     * Fetch templates for technical properties
     * @type {(function(): void)}
     */
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        const response = await Api.listIndustryPropertyTemplate(industryId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response.resultFlag) {
            setTemplates(response.data);
        }
    }, [isMounted, industryId])

    /**
     * Fetch templates on the component mount
     */
    useEffect(() => {
        if (!industryId)
            return;
        void fetchTemplates();
    }, [fetchTemplates, industryId])

    /**
     * Remove a template
     * @type {(function(*): Promise<void>)}
     */
    const removeTemplate = useCallback(async (templateId) => {
        setRemovingIds(prevState => [...prevState, templateId]);
        const response = await Api.deleteIndustryPropertyTemplate(templateId);
        if (!isMounted())
            return;
        setRemovingIds(prevState => prevState.filter(e => e !== templateId));
        if (response.resultFlag) {
            setTemplates(prevState => prevState.filter(e => e.id !== templateId));
        }
    }, [isMounted])

    /**
     * Invokes the [onSelect] callback with the selected template.
     */
    const onTemplateSelected = useCallback(async (e) => {
        const template = templates.find(a => a.id.toString() === e.target.value.toString()) ?? null
        if (!template)
            return;
        const response = await Api.getIndustryPropertyTemplate(template.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onTemplateSelectedProps({
                ...template,
                selectedPropertyIds: response.data.industryProperties.map(e => e.id),
            });
        }
    }, [isMounted, onTemplateSelectedProps, templates]);

    return (
        <>
            <TextField
                select
                size={'small'}
                disabled={loading || disabled}
                value={selectedTemplateId}
                variant={'outlined'}
                onChange={onTemplateSelected}
                SelectProps={{
                    renderValue: () => loading
                        ? "Loading templates..."
                        : selectedTemplateId
                            ? templates.find(e => e.id === selectedTemplateId)?.title
                            : "Select from saved templates",
                    displayEmpty: true,
                }}
            >
                {
                    loading &&
                    Array.from({length: 4}).map((_, a) => (
                        <MenuItem key={a} disabled value={undefined} sx={{paddingBlock: 0}}>
                            <Skeleton
                                variant="rectangular"
                                width={'100%'}
                                sx={(theme) => ({minHeight: 80, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                            />
                        </MenuItem>
                    ))
                }
                {
                    !loading && !templates.length &&
                    <MenuItem disabled value={undefined}>
                        <Typography variant={'body2'}>
                            No templates found.
                        </Typography>
                    </MenuItem>
                }
                {
                    !loading &&
                    templates.map(template => {
                        const disabled = removingIds.includes(template.id);
                        return (
                            <MenuItem
                                key={template.id}
                                value={template.id}
                                disabled={disabled}
                                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}
                            >
                                <Typography variant={'body2'}>
                                    {template.title}
                                </Typography>
                                <IconButton
                                    color={'error'}
                                    size={'small'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        removeTemplate(template.id);
                                        setSelectedTemplateId(prev => prev === template.id ? null : prev);
                                    }}
                                    disabled={disabled}
                                >
                                    {
                                        disabled
                                            ? <CircularProgress size={20} color={'error'}/>
                                            : <DeleteOutlined/>
                                    }
                                </IconButton>
                            </MenuItem>
                        );
                    })
                }
            </TextField>
        </>
    );
})


const ManageProductTechnicalPropertiesDialogProperty = ({property, onUpdate, onRemove, removing, onSelectionToggled, typeNames}) => {
    const [loadingAffectedProducts, setLoadingAffectedProducts] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Removes the property but first fetches the number of affected products.
     * @type {(function(): Promise<void>)}
     */
    const removeProperty = useCallback(async () => {
        setLoadingAffectedProducts(true);
        const response = await Api.getIndustryPropertyRelatedPartsCount(property.id)
        if (!isMounted())
            return;
        setLoadingAffectedProducts(false);
        onRemove(property, response?.data?.numberOfUsage ?? 0);
    }, [isMounted, onRemove, property])

    const propertyTypeTitle = useMemo(() => typeNames[property.typeId], [property.typeId, typeNames]);

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 1}} className={'manage-product-technical-properties-dialog-card'}>
                <Grid container spacing={1} sx={{margin: 0}}>
                    <Grid xs={'auto'}>
                        {
                            property.selectionReadonly &&
                            <Tooltip
                                title={"Already added to product"}
                                className={'cursor-not-allowed-hover'}
                                disableInteractive
                            >
                            <span>
                                <Checkbox
                                    className={'cursor-not-allowed-hover'}
                                    labelClassName={'cursor-not-allowed-hover m-0'}
                                    value={true}
                                    checked={property.selected}
                                    onChange={() => false}
                                    icon={<Icon/>}
                                    checkedIcon={<IconCheckbox/>}
                                    classes={{
                                        root: 'p-0',
                                    }}
                                />
                            </span>
                            </Tooltip>
                        }
                        {
                            !property.selectionReadonly &&
                            <Checkbox
                                value={true}
                                checked={property.selected ?? false}
                                onChange={() => onSelectionToggled(property)}
                                labelClassName={'m-0'}
                                classes={{
                                    root: 'p-0',
                                }}
                            />
                        }
                    </Grid>
                    <Grid xs>
                        <Grid container spacing={2} columns={12}>
                            <Grid xs={(onUpdate || onRemove) ? true : 12}>
                                <Typography variant={'body1'}>
                                    {property.title ?? ""}
                                    <Typography
                                        component={'span'}
                                        sx={{display: 'inline-block', marginInlineStart: 1}}
                                        variant={'body2'}
                                        color={'secondary.main'}
                                    >
                                        ({propertyTypeTitle})
                                    </Typography>
                                </Typography>
                            </Grid>
                            {
                                (onUpdate || onRemove) &&
                                <Grid xs={'auto'} className={'d-flex align-items-center justify-content-end p-0'}>
                                    {
                                        onUpdate &&
                                        <IconButton
                                            color={'paper.primary'}
                                            size={'small'}
                                            onClick={() => onUpdate(property)}
                                        >
                                            <EditOutlined/>
                                        </IconButton>
                                    }
                                    {
                                        onRemove &&
                                        <IconButton
                                            color={'paper.error'}
                                            size={'small'}
                                            disabled={removing || loadingAffectedProducts}
                                            onClick={() => removeProperty()}
                                        >
                                            {
                                                loadingAffectedProducts || removing
                                                    ? <CircularProgress size={20} color={'error'}/>
                                                    : <DeleteOutlined/>
                                            }
                                        </IconButton>
                                    }
                                </Grid>
                            }
                            <Grid xs={12}>
                                <Typography variant={'body2'}>
                                    Description: {property.description ?? "--"}
                                </Typography>
                            </Grid>
                            <Grid xs={12} className={'d-flex align-items-center flex-wrap'}>
                                <div className={classnames('checkmark-container')}>
                                    {
                                        property.hasPredefinedValues
                                            ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                            : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                    }
                                    <Typography variant={'caption'}>
                                        Has Predefined values
                                    </Typography>
                                </div>
                                <div className={classnames('checkmark-container')}>
                                    {
                                        property.isMultiSelect
                                            ? <SaveIcon className={classnames(`save`, "mr-1")}/>
                                            : <CancelIcon className={classnames(`cancel`, "mr-1")}/>
                                    }
                                    <Typography variant={'caption'}>
                                        Multi-select
                                    </Typography>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
}

export default ManageProductTechnicalPropertiesDialog;
